import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isNative } from '@whelk/const/src/env';
import type { NegativeReview } from '@whelk/services/src/common';
import {
  ChatModel,
  INotificationLiveModalConfig,
  TutorialLinkInfo,
} from '@whelk/services/src/type/chat';

interface ConfigState {
  chatModelList: ChatModel[];
  communityQRCodeURL: string;
  mobileDownloadQRCode: string;
  shareQrcodeOpen: boolean; // 是否展示二维码
  shareQrcodeUrl: string; // 分享二维码链接
  selectMaxCount: number; // 选择的最大次数
  isShowFeedBack: boolean;
  isShowFollowUp: boolean;
  isShowPlayAudio: boolean;
  isShowRetryButton: boolean;
  isShowShareButton: boolean;
  negativeReviewsList: NegativeReview;
  groupConfigUrl: string;
  tutorialLinkInfo: TutorialLinkInfo | undefined;
  nativeStyle: {
    statusBarHeight: number;
    navigationBarHeight: number;
  };
  showThinkButton: boolean;
  defaultModel: string;
  notificationLiveModalConfig: INotificationLiveModalConfig;
  questionnaireUrl: string;
}

const initialState: ConfigState = {
  mobileDownloadQRCode: '', // 手机端下载二维码
  communityQRCodeURL: '', // 联系我们 二维码
  shareQrcodeOpen: false, // 是否展示二维码
  shareQrcodeUrl: '', // 分享二维码链接
  selectMaxCount: 20 as number, // 选择的最大次数

  isShowFeedBack: true,
  isShowFollowUp: true,
  isShowPlayAudio: true,
  isShowRetryButton: true,
  isShowShareButton: true,
  negativeReviewsList: {} as NegativeReview,

  groupConfigUrl: '',
  chatModelList: [],
  tutorialLinkInfo: undefined as TutorialLinkInfo | undefined, // 教程链接信息
  nativeStyle: {
    statusBarHeight: isNative ? 33 : 0, // 鸿蒙顶部操作栏默认高度 33px
    navigationBarHeight: 0,
  },
  showThinkButton: false,
  defaultModel: 'mm-01',
  notificationLiveModalConfig: {} as INotificationLiveModalConfig,
  questionnaireUrl: '',
};

export const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    setCommunityQRCodeURL: (state, action: PayloadAction<string>) => {
      state.communityQRCodeURL = action.payload;
    },
    setMobileDownloadQRCode: (state, action: PayloadAction<string>) => {
      state.mobileDownloadQRCode = action.payload;
    },
    setShareQrcodeOpen: (state, action: PayloadAction<boolean>) => {
      state.shareQrcodeOpen = action.payload;
    },
    setShareQrcodeUrl: (state, action: PayloadAction<string>) => {
      state.shareQrcodeUrl = action.payload;
    },
    setSelectMaxCount: (state, action: PayloadAction<number>) => {
      state.selectMaxCount = action.payload;
    },
    setIsShowFeedBack: (state, action: PayloadAction<boolean>) => {
      state.isShowFeedBack = action.payload;
    },
    setIsShowFollowUp: (state, action: PayloadAction<boolean>) => {
      state.isShowFollowUp = action.payload;
    },
    setIsShowPlayAudio: (state, action: PayloadAction<boolean>) => {
      state.isShowPlayAudio = action.payload;
    },
    setIsShowRetryButton: (state, action: PayloadAction<boolean>) => {
      state.isShowRetryButton = action.payload;
    },
    setIsShowShareButton: (state, action: PayloadAction<boolean>) => {
      state.isShowShareButton = action.payload;
    },
    setNegativeReviewsList: (state, action: PayloadAction<NegativeReview>) => {
      state.negativeReviewsList = action.payload;
    },
    setGroupConfig: (state, action: PayloadAction<string>) => {
      state.groupConfigUrl = action.payload;
    },
    setTutorialLinkInfo: (state, action: PayloadAction<TutorialLinkInfo | undefined>) => {
      state.tutorialLinkInfo = action.payload;
    },
    setChatModelList: (state, action: PayloadAction<ChatModel[]>) => {
      state.chatModelList = action.payload;
    },
    setShowThinkButton: (state, action: PayloadAction<boolean>) => {
      state.showThinkButton = action.payload;
    },
    setDefaultModel: (state, action: PayloadAction<string>) => {
      state.defaultModel = action.payload;
    },
    setNativeStyle: (
      state,
      action: PayloadAction<{ statusBarHeight: number; navigationBarHeight: number }>,
    ) => {
      state.nativeStyle = action.payload;
    },
    setNotificationLiveModalConfig: (
      state,
      action: PayloadAction<INotificationLiveModalConfig>,
    ) => {
      state.notificationLiveModalConfig = action.payload;
    },
    setQuestionnaireUrl: (state, action: PayloadAction<string>) => {
      state.questionnaireUrl = action.payload;
    },
  },
});

export const {
  setIsShowFeedBack,
  setIsShowFollowUp,
  setIsShowPlayAudio,
  setIsShowRetryButton,
  setIsShowShareButton,
  setCommunityQRCodeURL,
  setMobileDownloadQRCode,
  setShareQrcodeOpen,
  setShareQrcodeUrl,
  setNegativeReviewsList,
  setGroupConfig,
  setSelectMaxCount,
  setTutorialLinkInfo,
  setChatModelList,
  setShowThinkButton,
  setDefaultModel,
  setNativeStyle,
  setNotificationLiveModalConfig,
  setQuestionnaireUrl,
} = configSlice.actions;

export default configSlice.reducer;
